import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { useDemo, InviteStatus } from '../../contexts/DemoContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { ASSESSMENT_URL, assessmentHref, participants } from '../../data/demo';
const STATUS_STYLE: Record<InviteStatus, string> = {
  'NOT STARTED': 'border-rule text-muted',
  'IN PROGRESS': 'border-signal-amber/50 text-signal-amber',
  SUBMITTED: 'border-brand/40 text-brand',
  'AWAITING REVIEW': 'border-brand text-brand'
};
export function Invite() {
  const {
    mode,
    candidateCount,
    inviteStatuses,
    setInviteStatuses
  } = useDemo();
  const [copied, setCopied] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();
  const rows = participants.slice(0, mode === 'single' ? 1 : Math.max(2, candidateCount));
  const simulate = () => {
    if (running) return;
    setRunning(true);
    const fill = (s: InviteStatus) => setInviteStatuses(rows.map(() => s));
    window.setTimeout(() => fill('IN PROGRESS'), 300);
    window.setTimeout(() => fill('SUBMITTED'), 1600);
    window.setTimeout(() => fill('AWAITING REVIEW'), 2700);
    window.setTimeout(() => navigate('/demo/record'), 3400);
  };
  const copy = (token: string) => {
    navigator.clipboard?.writeText(assessmentHref());
    setCopied(token);
    window.setTimeout(() => setCopied(null), 1500);
  };
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-656" data-mp="27vi5gsl7ll4m">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-657" data-mp="27t5iu24iaml8">
        <LenticMark data-id="element-658" data-mp="2mkwhy9k30io8" />
        <BackLink to="/demo/instrument" label="Back to the instrument" data-id="element-659" data-mp="2mlfrb1vo1yqn" />
      </div>

      <main className="mx-auto w-full max-w-[860px] flex-1 px-8 py-14" data-id="element-660" data-mp="2hrtoe1fb1981">
        <p className="eyebrow text-brand" data-id="element-661" data-mp="1u320vncntf1l">Invite</p>
        <h1 className="mt-3 text-[26px] font-semibold tracking-tight text-ink" data-id="element-662" data-mp="1u1h6bxqchw3q">
          {rows.length === 1 ? 'One tokenised link.' : `${rows.length} tokenised links.`}
        </h1>
        <p className="mt-2 max-w-lg text-[13.5px] leading-relaxed text-muted" data-id="element-663" data-mp="3p6qc7fzzr5y7">
          Identity stays in your enrolment list. The assessment record never carries a name.
        </p>

        <ul className="mt-8 divide-y divide-rule border-y border-rule" data-id="element-664" data-mp="2rxhknfrwxz8">
          {rows.map((p, i) => {
          const status = inviteStatuses[i] || 'NOT STARTED';
          return <li key={p.id} className="grid grid-cols-[110px_minmax(0,1fr)_auto_auto] items-center gap-4 py-4" data-id="element-665" data-mp="369fngpbzjux0" data-mpi={i}>
                <span className="font-mono-num text-[12px] text-ink" data-id="element-666" data-mp="3b7qvcuipzvcl">{p.id}</span>
                <a href={ASSESSMENT_URL} target="_blank" rel="noreferrer" className="truncate font-mono-num text-[11.5px] text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:text-ink hover:decoration-brand" data-id="element-667" data-mp="3f812wbm5uttk">
                  {p.token}
                </a>
                <button type="button" onClick={() => copy(p.token)} className="inline-flex items-center gap-1.5 rounded-sm border border-rule bg-white px-2.5 py-1.5 font-mono-num text-[10.5px] uppercase tracking-wider text-muted transition-colors duration-150 ease-settle hover:border-ink hover:text-ink" data-id="element-668" data-mp="19ka3hrr2ds00">
                  {copied === p.token ? <CheckIcon className="h-3 w-3" data-id="element-669" data-mp="qvwrx6t55rwv" /> : <CopyIcon className="h-3 w-3" data-id="element-670" data-mp="qt0tlhddt17w" />}
                  {copied === p.token ? 'copied' : 'copy'}
                </button>
                <span className={`w-[132px] rounded-sm border px-2.5 py-1 text-center font-mono-num text-[10.5px] uppercase tracking-wider transition-colors duration-150 ease-settle ${STATUS_STYLE[status]}`} data-id="element-671" data-mp="gwe8qsl22a5v">
                  {status}
                </span>
              </li>;
        })}
        </ul>

        <p className="mt-4 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-672" data-mp="27a51m0stc6r3">
          Demo: all invite links open the same FIN-001 build. Production issues one unique tokenised
          session per candidate.
        </p>

        <button type="button" onClick={simulate} disabled={running} className="mt-6 font-mono-num text-[11px] uppercase tracking-[0.12em] text-brand underline decoration-rule underline-offset-4 transition-colors hover:decoration-brand disabled:text-muted" data-id="element-673" data-mp="25bvkxdyly7aj">
          {running ? 'simulating…' : 'simulate completion'}
        </button>
      </main>

      <DemoFooter data-id="element-674" data-mp="1lnway5bvb466" />
    </div>;
}